import React, { useEffect, useState } from "react";
import axios from "axios";
import { Inbox } from "lucide-react";
import GlassCard from "../ui-custom/GlassCard";
import Reveal from "../ui-custom/Reveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

// Renders POs / Invoices / Quotations for the logged-in customer/vendor,
// sourced live from the Google Sheet. Columns are dynamic (whatever the sheet
// tab defines). Shows the standard "no records" message when nothing matches.
const SheetRecordsTable = ({ endpoint }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setFailed(false);
    axios
      .get(`${API}${endpoint}`, { withCredentials: true })
      .then((res) => {
        if (active) {
          setData(res.data);
          setLoading(false);
        }
      })
      .catch(() => {
        if (active) {
          setFailed(true);
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [endpoint]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 rounded-full border-2 border-qcyan/30 border-t-qcyan animate-spin" />
      </div>
    );
  }

  const rows = data?.rows || [];
  const columns = data?.columns || [];
  const hasRecords = !failed && data?.matched && rows.length > 0;

  if (!hasRecords) {
    return (
      <Reveal>
        <GlassCard className="p-10 text-center">
          <span className="w-14 h-14 rounded-full bg-qcyan/10 border border-qcyan/25 flex items-center justify-center mx-auto mb-5">
            <Inbox size={24} className="text-qcyan" />
          </span>
          <p className="text-foreground font-medium">
            No records are currently linked to this email. Please contact admin.
          </p>
        </GlassCard>
      </Reveal>
    );
  }

  return (
    <Reveal>
      <GlassCard className="overflow-hidden">
        <div>
          <table className="w-full table-fixed text-sm">
            <thead>
              <tr className="text-left text-muted-foreground uppercase text-xs tracking-wide border-b border-border/10">
                {columns.map((c) => (
                  <th key={c} className="px-6 py-4 break-words">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={i}
                  className="border-b border-border/5 hover:bg-foreground/[0.03] transition-colors"
                >
                  {columns.map((c, ci) => (
                    <td
                      key={c}
                      className={
                        ci === 0
                          ? "px-6 py-4 font-display text-qcyan font-semibold break-words align-top"
                          : "px-6 py-4 text-muted-foreground break-words align-top"
                      }
                    >
                      {row[c]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </GlassCard>
    </Reveal>
  );
};

export default SheetRecordsTable;
